import qz from 'qz-tray';

/**
 * Service to manage connection and interactions with local QZ Tray instance.
 */
class QzService {
  constructor() {
    this.connectionPromise = null;
  }

  /**
   * Connect to the local QZ Tray WebSocket server if not already connected.
   */
  async connect() {
    if (qz.websocket.isActive()) {
      return qz;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = qz.websocket.connect()
      .then(() => {
        this.connectionPromise = null;
        console.log('Connected to QZ Tray WebSocket');
        return qz;
      })
      .catch((err) => {
        this.connectionPromise = null;
        console.error('Failed to connect to QZ Tray:', err);
        throw new Error(
          'Could not connect to QZ Tray. Please make sure the QZ Tray application is installed and running on your computer (typically on port 8182).'
        );
      });

    return this.connectionPromise;
  }

  /**
   * Disconnect from the local QZ Tray WebSocket server.
   */
  async disconnect() {
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect();
      console.log('Disconnected from QZ Tray WebSocket');
    }
  }

  /**
   * Get the list of all available printers on the local computer.
   */
  async getLocalPrinters() {
    try {
      await this.connect();
      // qz.printers.find() with no arguments returns a list of all local printers
      const printers = await qz.printers.find();
      return Array.isArray(printers) ? printers : [printers];
    } catch (err) {
      console.error('Error fetching local printers from QZ Tray:', err);
      throw err;
    }
  }

  /**
   * Direct print a PDF base64 string to a specific local printer.
   * @param {string} printerName - Exact name of the printer on local system
   * @param {string} base64Data - Base64 encoded string of the PDF file (without data url prefix)
   * @param {Object} options - Configuration overrides (e.g. copies, paperSize, margins)
   */
  async printPdf(printerName, base64Data, options = {}) {
    if (!printerName) {
      throw new Error('Printer name is required for QZ Tray printing');
    }

    try {
      await this.connect();

      // Configure printing parameters
      const configOpts = {
        copies: options.copies || 1,
        jobName: options.jobName || 'Accounting Document',
        ...options.configOverrides,
      };

      // Create printer config
      const config = qz.configs.create(printerName, configOpts);

      // Prepare pixel print data of type PDF and base64 flavor
      const printData = [
        {
          type: 'pixel',
          format: 'pdf',
          flavor: 'base64',
          data: base64Data,
        },
      ];

      // Send to printer
      const result = await qz.print(config, printData);
      return result;
    } catch (err) {
      console.error(`QZ Tray printing failed for printer "${printerName}":`, err);
      throw new Error(err.message || 'Printing failed. Please check your printer connection and settings.');
    }
  }

  /**
   * Direct print a base64 image to a specific local printer.
   * @param {string} printerName - Exact name of the printer on local system
   * @param {string} base64Data - Base64 encoded string of the image (without data url prefix)
   * @param {Object} options - Configuration overrides (e.g. copies)
   */
  async printImage(printerName, base64Data, options = {}) {
    if (!printerName) {
      throw new Error('Printer name is required for QZ Tray printing');
    }

    try {
      await this.connect();

      const configOpts = {
        copies: options.copies || 1,
        jobName: options.jobName || 'Barcode Print',
        ...options.configOverrides,
      };

      const config = qz.configs.create(printerName, configOpts);

      const printData = [
        {
          type: 'pixel',
          format: 'image',
          flavor: 'base64',
          data: base64Data,
        },
      ];

      const result = await qz.print(config, printData);
      return result;
    } catch (err) {
      console.error(`QZ Tray printing failed for printer "${printerName}":`, err);
      throw new Error(err.message || 'Printing failed. Please check your printer connection and settings.');
    }
  }
}

const qzService = new QzService();
export default qzService;
